import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Drop, Lock, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function PayInvoice() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => { api.publicInvoice(id).then(setInv).catch(() => setNotFound(true)); }, [id]);

  const pay = async () => {
    setLoading(true);
    try { const { checkout_url } = await api.checkout(id); window.location.href = checkout_url; }
    catch (e) { toast.error(e.response?.data?.detail || "Unable to start payment"); setLoading(false); }
  };

  if (notFound) return <Center><p className="text-slate-500">Invoice not found.</p></Center>;
  if (!inv) return <Center><p className="text-slate-400">Loading invoice…</p></Center>;

  return (
    <div className="min-h-screen bg-slate-100 py-10 px-4">
      <div className="max-w-lg mx-auto">
        <div className="flex items-center justify-center mb-6">
          <img src="/adpw-logo.png" alt="All Day Power Washing" className="h-24 w-auto" />
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="bg-[#0B1726] px-6 py-5 flex items-center justify-between">
            <div><div className="text-white font-display font-bold text-lg">Invoice {inv.number}</div><div className="text-slate-400 text-xs">Billed to {inv.client_name}</div></div>
            {inv.status === "paid" && <span className="inline-flex items-center gap-1 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full"><CheckCircle size={14} weight="fill" /> PAID</span>}
          </div>

          <div className="p-6">
            <table className="w-full mb-4">
              <thead><tr className="text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-200"><th className="text-left pb-2">Service</th><th className="text-center pb-2">Qty</th><th className="text-right pb-2">Amount</th></tr></thead>
              <tbody>
                {inv.line_items.map((li, i) => (
                  <tr key={i} className="border-b border-slate-50"><td className="py-2.5 text-sm text-slate-700">{li.description}</td><td className="py-2.5 text-center text-sm text-slate-500">{li.quantity}</td><td className="py-2.5 text-right text-sm font-medium">{money(li.quantity * li.unit_price)}</td></tr>
                ))}
              </tbody>
            </table>
            <div className="space-y-1.5">
              <Row label="Subtotal" value={money(inv.subtotal)} />
              <Row label={`Tax (${inv.tax_rate}%)`} value={money(inv.tax)} />
              <div className="flex justify-between pt-3 mt-1 border-t-2 border-[#0B1726]"><span className="font-bold text-slate-900">Total Due</span><span className="font-display font-extrabold text-2xl text-[#00B4D8]">{money(inv.total)}</span></div>
            </div>

            {inv.status !== "paid" ? (
              <Button onClick={pay} disabled={loading} className="w-full mt-6 h-12 bg-[#00B4D8] hover:bg-cyan-600 text-base font-semibold" data-testid="pay-now-button">
                <Lock size={18} weight="fill" className="mr-2" /> {loading ? "Redirecting…" : `Pay ${money(inv.total)} Securely`}
              </Button>
            ) : (
              <div className="mt-6 text-center text-emerald-600 font-semibold flex items-center justify-center gap-2"><CheckCircle size={20} weight="fill" /> This invoice has been paid. Thank you!</div>
            )}
            <p className="text-center text-xs text-slate-400 mt-4">Secure payment powered by Stripe</p>
            <p className="text-center text-xs text-slate-400 mt-1">Questions? Call us at <a href="tel:+17196446105" className="text-[#00B4D8] hover:underline">(719) 644-6105</a></p>
          </div>
        </div>
      </div>
    </div>
  );
}

const Center = ({ children }) => <div className="min-h-screen flex items-center justify-center bg-slate-100">{children}</div>;
const Row = ({ label, value }) => <div className="flex justify-between text-sm"><span className="text-slate-500">{label}</span><span className="font-medium text-slate-700">{value}</span></div>;
